// src/ctxReflector/components/Instances/views/Licensed/UnlicensedPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { UnlicensedInstance, columns as baseColumns } from "./UnlicensedData";
import {
  fetchUnlicensedInstances as getAllInstances,
  deleteLicensedInstance as removeDevice,
} from "./request/request";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeUnlicensedFields } from "./unlicensedFieldSpec";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

function UnlicensedPage() {
  const { isAuthenticated, isLoading } = useAuth0();
  const api = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const [instances, setInstances] = useState<UnlicensedInstance[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const unlicensedFields = useMemo(() => makeUnlicensedFields(), []);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [instanceToDelete, setInstanceToDelete] =
    useState<UnlicensedInstance | null>(null);

  const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    loadInstances();
  }, [isLoading, isAuthenticated, tenantId]);

  const loadInstances = async () => {
    try {
      setIsFetching(true);
      const resp = await getAllInstances(api, tenantId);
      setInstances(resp.data || []);
    } catch (error) {
      console.error("Error fetching Unlicensed Instances =>", error);
    } finally {
      setIsFetching(false);
    }
  };

    // Generate suggested keys from field labels
    const suggestedKeys = useMemo(() => {
      return unlicensedFields.map((f) => f.label);
    }, [unlicensedFields]);
  
    // Build maps for filter normalization
    const fieldById = useMemo(() => {
      const m = new Map<string, FilterField<UnlicensedInstance>>();
      for (const f of unlicensedFields) {
        const id = (f.key ?? labelId(f.label)).toLowerCase();
        m.set(id, f);
      }
      return m;
    }, [unlicensedFields]);
  
    const keyMap = useMemo(() => {
      const m = new Map<string, string>();
      for (const f of unlicensedFields) {
        const id = (f.key ?? labelId(f.label)).toLowerCase();
        const label = f.label.toLowerCase();
        m.set(id, id);
        m.set(label, id);
      }
      return m; 
    }, [unlicensedFields]);
  
  // Normalize filters
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<UnlicensedInstance>(normalizedFilters, unlicensedFields),
    [normalizedFilters, unlicensedFields]
  );

  // Apply predicate to filter data
  const filteredInstances = useMemo(
    () => instances.filter(predicate),
    [instances, predicate]
  );


  const handleDeleteClick = (rowData: UnlicensedInstance) => {
    setInstanceToDelete(rowData);
    setDeleteConfirmInput("");
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!instanceToDelete) return;
    if (deleteConfirmInput.trim().toLowerCase() !== "confirm") {
      setIsDeleteConfirmOpen(false);
      showLicenseSafe();
      return;
    }
    setIsDeleteConfirmOpen(false);
    try {
      setIsFetching(true);
      await removeDevice(api, instanceToDelete.id, tenantId);
      setInstances((prev) =>
        prev.filter((inst) => inst.id !== instanceToDelete.id)
      );
      setAlertVariant("success");
      setAlertTitle("Deleted!");
      setAlertDescription(
        `La instancia "${instanceToDelete.name}" se ha eliminado.`
      );
    } catch (err) {
      console.error("Error removing device =>", err);
      setAlertVariant("error");
      setAlertTitle("Error");
      setAlertDescription(
        "No se pudo eliminar la instancia, intente de nuevo."
      );
    } finally {
      setIsFetching(false);
      setInstanceToDelete(null);
      setIsResultAlertOpen(true);
    }
  };

  const showLicenseSafe = () => {
    setAlertVariant("success");
    setAlertTitle("Your Device is safe!");
    setAlertDescription("No se realizó ningún cambio.");
    setIsResultAlertOpen(true);
  };

  const finalColumns = baseColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: UnlicensedInstance) => (
          <button
            className="
              border border-semantic-border-secondary
              rounded
              bg-semantic-bg-danger-secondary
              text-semantic-fg-danger
              hover:bg-red-100
              w-10 h-10 flex items-center justify-center
            "
            onClick={() => handleDeleteClick(row)}
          >
            {/* ícono de basura */}
          </button>
        ),
      };
    }
    return col;
  });

  return (
    <div className="relative p-4">
      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              <div className="flex items-center mb-4 gap-2">
                <div className="w-full max-w-full sm:max-w-[500px]">
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., Name: CTX-R)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFilters}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>
              </div>
                {isFetching ? (
                    <div className="p-4">
                        <SkeletonTableRows
                            rows={5}
                            cols={5}
                            colWidths={["200px"]}
                            rowHeight={15}
                            rowGapClassName="space-y-3"
                        />
                    </div>
                ) : (
                <GenericTable
                  columns={finalColumns}
                  data={filteredInstances}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10, 20]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                />
              )}
            </>
          }
        />
      </div>

      <GenericAlert
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="Once deleted, you will not be able to recover this Device! Enter 'confirm' to continue..."
        cancelLabel="Cancel"
        confirmLabel="Yes, delete it!"
        variant="warning"
        confirmButtonDisabled={
          deleteConfirmInput.trim().toLowerCase() !== "confirm"
        }
      >
        <div className="mt-4">
          <input
            className="border border-semantic-border-secondary rounded px-2 py-1 w-full"
            placeholder="confirm"
            value={deleteConfirmInput}
            onChange={(e) => setDeleteConfirmInput(e.target.value)}
          />
        </div>
      </GenericAlert>

      <GenericAlert
        isOpen={isResultAlertOpen}
        onClose={() => setIsResultAlertOpen(false)}
        onConfirm={() => setIsResultAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        cancelLabel="Close"
        confirmLabel="OK"
        variant={alertVariant}
      />
{/* 
      {overlay} */}
    </div>
  );
}

export default UnlicensedPage;
